import {
  Box,
  ConditionalWrapper,
  Flex,
  Loading,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { useEffect, useMemo } from 'react';

import { useNavigateBack } from '@jane/shared/hooks';

import {
  CartCheckoutError,
  CustomerInfoScreen,
  LoginScreen,
  MFAScreen,
  Modal,
  PaymentSelectionScreen,
  QRCodeCallout,
} from '../../components';
import { useHideVirtualKeyboard, useKioskSpacing } from '../../hooks';
import { SCREEN_NAMES, useCheckout, useIdleContext } from '../../providers';
import qrPng from '../assets/qr-logo.png';

export const Checkout = () => {
  const { getKioskSpacing, isIpad } = useKioskSpacing();
  const { setTimerDisabled } = useIdleContext();
  const {
    bankAccounts,
    error,
    formMethods,
    screen,
    setError,
    setSelectedPayment,
    setScreen,
    store,
  } = useCheckout();
  useHideVirtualKeyboard();

  const showJanePay =
    !!store?.kiosk_allow_auth && !!store.jane_pay_integration?.enabled;

  const mobileLinkBankUrl = useMemo(() => {
    const { hostname, port, protocol } = window.location;

    return `${protocol}//${hostname}${port ? `:${port}` : ''}/stores/${
      store.id
    }/mobile/link-bank?step=signed-in-welcome&source=checkout`;
  }, [store.id]);

  const goBack = useNavigateBack(`stores/${store.id}/menu`);

  const returnFromQRCode = () => {
    setScreen(
      screen.includes('payment')
        ? SCREEN_NAMES.payment
        : SCREEN_NAMES.customerInfo
    );
    setTimerDisabled(false);
  };

  // watch for linked bank accounts to close the QR code callout
  useEffect(() => {
    if (bankAccounts?.length > 0) {
      returnFromQRCode();
    }
  }, [bankAccounts]);

  // this sets the selectedPayment to the primary bank account once the aeropay data loads
  useEffect(() => {
    if (bankAccounts?.length > 0 && showJanePay) {
      const account = bankAccounts?.find((account) => account.is_selected);
      if (account?.account_last_4) {
        setSelectedPayment(account.account_last_4);
      }
    }
  }, [bankAccounts, showJanePay]);

  if (error)
    return <CartCheckoutError error={error} onClose={() => setError('')} />;

  return (
    <Modal
      appId="root"
      contentLabel="Checkout"
      onRequestClose={() => goBack()}
      open
      overflow="visible"
      variant="dialogue"
    >
      <Modal.Content overflowY="auto" padding={false}>
        <ConditionalWrapper
          condition={[
            SCREEN_NAMES.customerInfo,
            SCREEN_NAMES.payment,
            SCREEN_NAMES.mfa,
          ].includes(screen)}
          wrapper={(children) => (
            <Flex
              flexDirection="column"
              p={getKioskSpacing(32, 40)}
              width={isIpad ? 425 : 525}
            >
              <Typography mb={getKioskSpacing(24, 16)} variant="title-bold">
                Checkout
              </Typography>

              <Form.BaseForm
                formMethods={formMethods}
                name="Cart User Details"
                onSubmit={() => null}
              >
                {children}
              </Form.BaseForm>
            </Flex>
          )}
        >
          {screen === SCREEN_NAMES.loading ? (
            <Flex
              flexDirection="column"
              p={getKioskSpacing(32, 40)}
              width={isIpad ? 425 : 525}
              gap={getKioskSpacing(24, 16)}
            >
              <Typography variant="title-bold">Checkout</Typography>
              <Typography>Placing your order...</Typography>
              <Box position="relative" mt={12}>
                <Loading color="black" size="lg" />
              </Box>
            </Flex>
          ) : screen === SCREEN_NAMES.customerInfo ? (
            <CustomerInfoScreen />
          ) : screen === SCREEN_NAMES.payment ? (
            <PaymentSelectionScreen />
          ) : screen === SCREEN_NAMES.mfa ? (
            <MFAScreen />
          ) : screen.includes('login') ? (
            <LoginScreen onClick={returnFromQRCode} qrCode={qrPng} />
          ) : screen.includes('link-bank') ? (
            <QRCodeCallout
              type="link-bank"
              buttonLabel="Back to checkout"
              onClick={returnFromQRCode}
              qrCode={qrPng}
              url={mobileLinkBankUrl}
            />
          ) : (
            <></>
          )}
        </ConditionalWrapper>
      </Modal.Content>
    </Modal>
  );
};
